import * as React from "react";
import { MoreHorizontal } from "lucide-react";
import { Button } from "../Button";
import { DropdownMenu, MenuItem } from "../DropdownMenu";
import type { ContextAction, ContextActionsDisplay } from "./types";
import { useT } from "../../../app/lib/i18n";
import * as s from "./HeaderActionsRow.style.css";

export type HeaderActionsRowProps = {
  statusIndicators?: React.ReactNode[];
  contextActions?: ContextAction[];
  contextActionsDisplay?: ContextActionsDisplay;
  /** Trailing control (e.g. section expand/collapse chevron). */
  trailing?: React.ReactNode;
};

/**
 * Right-aligned header chrome:
 * `[statusIndicators…] [actions or menu] [trailing]`
 *
 * Buttons are ordered right→left (index 0 closest to trailing/edge).
 * Status indicators and the menu trigger remain visible when a parent section is collapsed.
 */
export function HeaderActionsRow({
  statusIndicators,
  contextActions,
  contextActionsDisplay = "buttons",
  trailing,
}: HeaderActionsRowProps) {
  const t = useT();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuAnchorRef = React.useRef<HTMLButtonElement>(null);

  const actions = contextActions ?? [];
  const indicators = statusIndicators ?? [];

  const handleMenuChange = (actionId: string) => {
    actions.find((a) => a.id === actionId)?.onClick();
  };

  return (
    <div className={s.row}>
      {indicators.length > 0 && (
        <div className={s.indicators}>
          {indicators.map((node, i) => (
            <div key={i} className={s.indicatorSlot}>
              {node}
            </div>
          ))}
        </div>
      )}

      {actions.length > 0 && contextActionsDisplay === "buttons" && (
        <div className={s.actionButtons}>
          {[...actions].reverse().map((action) => (
            <Button
              key={action.id}
              type="button"
              size="sm"
              color={action.color ?? "neutral"}
              variant="outline"
              disabled={action.disabled}
              iconLeft={action.icon}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}

      {actions.length > 0 && contextActionsDisplay === "menu" && (
        <>
          <button
            ref={menuAnchorRef}
            type="button"
            className={s.menuTrigger}
            aria-label={t("shared.ui.moreActions")}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((prev) => !prev);
            }}
          >
            <MoreHorizontal size={16} />
          </button>
          <DropdownMenu
            anchorEl={menuAnchorRef}
            anchorPos="SE"
            openDirection="SW"
            open={menuOpen}
            setOpen={setMenuOpen}
            onChange={handleMenuChange}
            closeOnSelect
          >
            {actions.map((action) => (
              <MenuItem key={action.id} value={action.id}>
                <span className={s.menuItemContent}>
                  {action.icon && <span className={s.menuItemIcon}>{action.icon}</span>}
                  {action.label}
                </span>
              </MenuItem>
            ))}
          </DropdownMenu>
        </>
      )}

      {trailing}
    </div>
  );
}
